import VpnKeyIcon from "@mui/icons-material/VpnKey";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, IconButton, Stack, Tab, Tooltip } from "@mui/material";
import type React from "react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { useApiQuery } from "../../hooks";
import { AgentsTab } from "./agents-tab";
import { ApiKeyModal } from "./api-key-dialog";
import { DemoCreditsDisplay } from "./demo-credits-display";
import { NewTestTab } from "./new-test-tab";
import { RegressionTab } from "./regression-tab";

interface Metrics {
  tests_count: number;
  sessions_count: number;
}

type TabValue = "new-test" | "regression" | "agents";

export const VibeTesting = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<TabValue>("new-test");
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);

  // Auto-open API key modal if showApiKey query param is true
  useEffect(() => {
    const showApiKey = searchParams.get("showApiKey");
    if (showApiKey === "true") {
      setApiKeyModalOpen(true);
      // Remove the query param after opening the modal
      searchParams.delete("showApiKey");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const { data: metrics } = useApiQuery<Metrics>("/vibe-qa/metrics", {
    refetchInterval: 5000,
    refetchOnWindowFocus: true,
  });

  const testsCount = metrics?.tests_count ?? 0;
  const sessionsCount = metrics?.sessions_count ?? 0;

  const handleTabChange = (_: React.SyntheticEvent, newValue: TabValue) => {
    setTab(newValue);
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        flex: 1,
        display: "flex",
        flexDirection: "column",
      }}
      data-testid="vibe-it"
    >
      <TabContext value={tab}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", position: "relative" }}>
          <Stack direction="row" alignItems="center" justifyContent="center">
            <TabList onChange={handleTabChange} aria-label="Vibe QA Tabs" centered>
              <Tab label="New test" value="new-test" />
              <Tab
                label={`Regression${testsCount > 0 ? ` (${testsCount})` : ""}`}
                value="regression"
              />
              <Tab
                label={`Agents${sessionsCount > 0 ? ` (${sessionsCount})` : ""}`}
                value="agents"
              />
            </TabList>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ position: "absolute", right: 16 }}
            >
              <DemoCreditsDisplay />
              <Tooltip title="View API Key">
                <IconButton onClick={() => setApiKeyModalOpen(true)} size="small">
                  <VpnKeyIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>

        <TabPanel value="new-test" sx={{ flex: 1, overflow: "auto" }}>
          <NewTestTab />
        </TabPanel>

        <TabPanel value="regression" sx={{ flex: 1, overflow: "auto" }}>
          <RegressionTab />
        </TabPanel>

        <TabPanel value="agents" sx={{ flex: 1, overflow: "auto" }}>
          <AgentsTab />
        </TabPanel>
      </TabContext>

      <ApiKeyModal open={apiKeyModalOpen} onClose={() => setApiKeyModalOpen(false)} />
    </Box>
  );
};
